import { combineLatest, NEVER, Observable, of as observableOf } from "rxjs";

import { catchError, map, switchMap, tap } from "rxjs/operators";
import { Injectable } from "@angular/core";

import { ActivatedRouteSnapshot, Router, RouterStateSnapshot } from "@angular/router";

import { CryptoWalletService } from "./wallet/crypto_wallet.service";
import { FiatWalletService } from "./wallet/fiat_wallet.service";
import { FiatWithdrawalFraudComponent } from "../components/step-summary/success-screens/fiat-withdraw-fraud/fiat-withdrawal-fraud.component";
import { ModalService } from "@core-services/modal.service";
import { InfoService } from "@core-services/info.service";
import { FiatTransactionSuccessComponent } from "../components/step-summary/success-screens/fiat-transaction-success/fiat-transaction-success.component";
import { SendSuccessComponent } from "../components/step-summary/success-screens/send-success/send-success.component";
import { ServiceNames } from "@models/service_enums";
import { InitService } from "@core-services/init.service";
import { WithdrawSuccessComponent } from "@modules/main/components/step-summary/success-screens/withdraw-success/withdraw-success.component";
import { FiatTransaction } from "@models/transaction";
import { FiatService } from "@main-services/currency";
import { LaunchDarklyService, LD_FLAG } from "@core-services/launch-darkly/launch-darkly.service";
import { SendMultichainMoneyOnWayComponent } from "@modules/main/components/step-summary/success-screens/send-multichain-money-on-way/send-multichain-money-on-way.component";

@Injectable({
	providedIn: "root"
})
export class FiatTransactionConfirmService {
	public constructor(
		private fiatWalletService: FiatWalletService,
		private fiatService: FiatService,
		private infoService: InfoService,
		private modalService: ModalService,
		private router: Router,
		private initService: InitService
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean | Observable<boolean> | Promise<boolean> {
		return this.initService
			.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
			.pipe(
				switchMap(() =>
					this.confirmFiatTransaction(route.params.pid, route.params.tid, route.params.confirmCode)
				)
			);
	}

	private confirmFiatTransaction(walletId: string, transactionId: string, confirmCode: string): Observable<boolean> {
		return this.fiatWalletService.confirmTransaction(walletId, transactionId, confirmCode).pipe(
			tap((transaction) => {
				this.router.navigate(["fiat-wallet", walletId]).then(() => {
					let _modalConfig: {
						component:
							| typeof FiatWithdrawalFraudComponent
							| typeof WithdrawSuccessComponent
							| typeof FiatTransactionSuccessComponent;
						inputs: { fiatTransaction: FiatTransaction } | { fiatName: string };
					};

					if (transaction.getType() === "withdrawal") {
						_modalConfig = transaction.isWithdrawalFraud()
							? {
									component: FiatWithdrawalFraudComponent,
									inputs: { fiatTransaction: transaction }
								}
							: {
									component: WithdrawSuccessComponent,
									inputs: {
										fiatName: this.fiatService.getItemById(transaction.getFiatId()).getName()
									}
								};
					} else {
						_modalConfig = {
							component: FiatTransactionSuccessComponent,
							inputs: { fiatTransaction: transaction }
						};
					}

					this.modalService
						.create<
							FiatWithdrawalFraudComponent | WithdrawSuccessComponent | FiatTransactionSuccessComponent
						>(_modalConfig.component, _modalConfig.inputs)
						.present();
				});
			}),
			map(() => true),
			catchError((error) => {
				this.router.navigate(["fiat-wallet", walletId, "transaction", transactionId]);
				this.infoService.setError(error);
				return observableOf(true);
			})
		);
	}
}

@Injectable({
	providedIn: "root"
})
export class FiatTransactionCancelService {
	public constructor(
		private fiatWalletService: FiatWalletService,
		private router: Router,
		private initService: InitService
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean | Observable<boolean> | Promise<boolean> {
		return this.initService
			.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
			.pipe(
				switchMap(() =>
					this.fiatWalletService.cancelById(route.params.pid, route.params.tid).pipe(
						map(() => true),
						catchError((error) => {
							this.router.navigate(["fiat-wallet", route.params.pid, "transaction", route.params.tid]);
							return observableOf(true);
						}),
						tap(
							() =>
								this.router.navigate([
									"fiat-wallet",
									route.params.pid,
									"transaction",
									route.params.tid
								]),
							() => NEVER
						)
					)
				)
			);
	}
}

@Injectable({
	providedIn: "root"
})
export class CryptoTransactionConfirmService {
	public constructor(
		private cryptoWalletService: CryptoWalletService,
		private infoService: InfoService,
		private modalService: ModalService,
		private router: Router,
		private initService: InitService,
		private launchDarkly: LaunchDarklyService
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean | Observable<boolean> | Promise<boolean> {
		return this.initService
			.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
			.pipe(
				switchMap(() =>
					this.confirmCryptoTransaction(route.params.pid, route.params.tid, route.params.confirmCode)
				)
			);
	}

	private confirmCryptoTransaction(
		walletId: string,
		transactionId: string,
		confirmCode: string
	): Observable<boolean> {
		return combineLatest([
			this.cryptoWalletService.confirmTransaction(walletId, transactionId, confirmCode),
			this.launchDarkly.isActive$(LD_FLAG.WITHDRAWAL_MULTICHAIN)
		]).pipe(
			map(([transaction, isMultichainActive]) => {
				this.router.navigate(["wallet", walletId]).then(() => {
					this.cryptoWalletService.updateWalletBalance(transaction);
					if (isMultichainActive) {
						this.modalService
							.create(SendMultichainMoneyOnWayComponent, { cryptoTransaction: transaction })
							.present();
					} else {
						this.modalService.create(SendSuccessComponent, { transaction }).present();
					}
				});
				return true;
			}),
			catchError((error) => {
				this.router.navigate(["wallet", walletId, "transaction", transactionId]);
				this.infoService.setError(error);
				return observableOf(true);
			})
		);
	}
}

@Injectable({
	providedIn: "root"
})
export class CryptoTransactionCancelService {
	public constructor(
		private cryptoWalletService: CryptoWalletService,
		private infoService: InfoService,
		private router: Router,
		private initService: InitService
	) {}

	public canActivate(
		route: ActivatedRouteSnapshot,
		state: RouterStateSnapshot
	): boolean | Observable<boolean> | Promise<boolean> {
		return this.initService
			.waitForInitServices([ServiceNames.MASTER_DATA, ServiceNames.INIT_ASSETS_SERVICE, ServiceNames.USER])
			.pipe(
				switchMap(() =>
					this.cryptoWalletService.cancelById(route.params.pid, route.params.tid).pipe(
						map(() => true),
						catchError((error) => {
							this.infoService.setError(error);
							this.router.navigate(["wallet", route.params.pid, "transaction", route.params.tid]);
							return observableOf(true);
						}),
						tap(() => {
							this.router.navigate(["wallet", route.params.pid, "transaction", route.params.tid]);
						})
					)
				)
			);
	}
}
